import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { BASE, getAuthHeaders } from '@/api/client'
import { useUploadToFolder, useDeleteImage, useBulkDeleteImages, useMoveImages, useUpdateFolder, useMediaFolders, useRenameImage, useBulkAntiFingerprint, useAntiFingerprintProgress } from '@/api/hooks/useMediaLibrary'
import { useStorageUsage } from '@/api/hooks/useStorage'
import { useQueryClient } from '@tanstack/react-query'
import {
  Upload, Trash2, Search, Scissors, Play, Eye, Check, Pencil,
  ImageIcon, Video, Loader2, CheckSquare, Square, X, Clock, CalendarPlus,
  FolderOpen, Tag, Plus, Shield, Download,
} from 'lucide-react'
import type { MediaFolder, MediaImage } from '@/types'

function formatTime(seconds: number) {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function getPublishStatus(img: MediaImage): 'none' | 'scheduled' | 'published' {
  if (!img.publishedTag || img.publishedTag.length === 0) return 'none'
  const now = Date.now()
  const hasPublished = img.publishedTag.some(t => {
    const d = new Date(t.date).getTime()
    return d <= now
  })
  if (hasPublished) return 'published'
  return 'scheduled'
}

interface MediaGridProps {
  folder: MediaFolder
  subfolders?: MediaFolder[]
  onPreview: (media: MediaImage) => void
  onCut: (media: MediaImage) => void
  onSchedule?: () => void
  onNavigateToFolder?: (folderId: string) => void
}

export function MediaGrid({ folder, subfolders = [], onPreview, onCut, onSchedule, onNavigateToFolder }: MediaGridProps) {
  const { uploadProgress, fileCount, ...uploadToFolder } = useUploadToFolder()
  const deleteImage = useDeleteImage()
  const bulkDelete = useBulkDeleteImages()
  const moveImagesMutation = useMoveImages()
  const updateFolder = useUpdateFolder()
  const renameImage = useRenameImage()
  const bulkAfp = useBulkAntiFingerprint()
  const { data: allFoldersRaw } = useMediaFolders()
  const { data: storageUsage } = useStorageUsage()
  const qc = useQueryClient()

  const [afpJobId, setAfpJobId] = useState<string | null>(null)
  const { data: afpProgress } = useAntiFingerprintProgress(afpJobId)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [search, setSearch] = useState('')
  const [mediaTypeFilter, setMediaTypeFilter] = useState('all')
  const [clipFilter, setClipFilter] = useState('all')
  const [publishedFilter, setPublishedFilter] = useState('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [selectMode, setSelectMode] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const dragCounter = useRef(0)
  const [newTag, setNewTag] = useState('')
  const [showTagInput, setShowTagInput] = useState(false)
  const uploadRef = useRef<HTMLInputElement>(null)
  const folderUploadRef = useRef<HTMLInputElement>(null)

  const allExistingTags = useMemo(() => {
    const tagSet = new Set<string>()
    allFoldersRaw?.forEach(f => f.tags?.forEach(t => tagSet.add(t)))
    return Array.from(tagSet).sort()
  }, [allFoldersRaw])

  const handleAddTag = () => {
    const tag = newTag.trim().replace(/^#/, '')
    if (!tag) return
    const currentTags = folder.tags || []
    if (currentTags.includes(tag)) {
      toast.error('Tag ja existe')
      return
    }
    updateFolder.mutate({ folderId: folder.id, tags: [...currentTags, tag] }, {
      onSuccess: () => { setNewTag(''); toast.success('Tag adicionada') },
    })
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const currentTags = (folder.tags || []).filter(t => t !== tagToRemove)
    updateFolder.mutate({ folderId: folder.id, tags: currentTags }, {
      onSuccess: () => toast.success('Tag removida'),
    })
  }

  const startRename = (img: MediaImage) => {
    setRenamingId(img.id)
    // Strip extension for editing
    const dotIdx = img.filename.lastIndexOf('.')
    setRenameValue(dotIdx > 0 ? img.filename.substring(0, dotIdx) : img.filename)
  }

  const submitRename = (img: MediaImage) => {
    const trimmed = renameValue.trim()
    if (!trimmed) { setRenamingId(null); return }
    // Restore extension
    const dotIdx = img.filename.lastIndexOf('.')
    const ext = dotIdx > 0 ? img.filename.substring(dotIdx) : ''
    const newFilename = trimmed + ext
    if (newFilename === img.filename) { setRenamingId(null); return }
    renameImage.mutate({ folderId: folder.id, imageId: img.id, filename: newFilename }, {
      onSuccess: () => { toast.success('Arquivo renomeado'); setRenamingId(null) },
      onError: () => toast.error('Erro ao renomear'),
    })
  }

  const handleAddSuggestion = (tag: string) => {
    const currentTags = folder.tags || []
    if (currentTags.includes(tag)) return
    updateFolder.mutate({ folderId: folder.id, tags: [...currentTags, tag] })
  }

  const filteredImages = useMemo(() => {
    return folder.images.filter(img => {
      if (search && !img.filename.toLowerCase().includes(search.toLowerCase())) return false
      if (mediaTypeFilter !== 'all' && img.mediaType !== mediaTypeFilter) return false
      if (clipFilter === 'clips' && !img.clip) return false
      if (clipFilter === 'originals' && img.clip) return false
      if (publishedFilter === 'published' && (!img.publishedTag || img.publishedTag.length === 0)) return false
      if (publishedFilter === 'unpublished' && img.publishedTag && img.publishedTag.length > 0) return false
      return true
    })
  }, [folder.images, search, mediaTypeFilter, clipFilter, publishedFilter])

  const handleUpload = useCallback((files: File[]) => {
    if (storageUsage && !storageUsage.isAdmin && storageUsage.quotaBytes) {
      const totalSize = files.reduce((s, f) => s + f.size, 0)
      const remaining = storageUsage.quotaBytes - storageUsage.usedBytes
      if (totalSize > remaining) {
        const remainingMB = Math.round(remaining / 1048576)
        const fileSizeMB = Math.round(totalSize / 1048576)
        toast.error(`Espaco insuficiente: ${fileSizeMB}MB selecionado, ${remainingMB}MB disponivel`, {
          description: 'Libere espaco ou compre mais armazenamento',
          duration: 8000,
        })
        return
      }
    }

    const movFiles = files.filter(f => f.name.toLowerCase().endsWith('.mov'))
    if (movFiles.length > 0) {
      const totalMB = Math.round(movFiles.reduce((s, f) => s + f.size, 0) / 1048576)
      toast.info(`${movFiles.length} arquivo(s) .mov (${totalMB}MB) — será convertido para .mp4 no servidor`, {
        description: 'Dica: envie direto em .mp4 para uploads mais rápidos',
        duration: 6000,
      })
    }
    uploadToFolder.mutate({ folderId: folder.id, files }, {
      onSuccess: (data: { added: number; failed?: string[]; failedCount?: number }) => {
        qc.invalidateQueries({ queryKey: ['storage-usage'] })
        if (data.failedCount && data.failedCount > 0) {
          toast.warning(`${data.added} enviado(s), ${data.failedCount} falharam`, {
            description: data.failed?.join('\n'),
            duration: 8000,
          })
        } else {
          toast.success(`${data.added} arquivo(s) enviado(s)!`)
        }
      },
      onError: (err: any) => {
        if (err?.status === 413 || err?.message?.includes('armazenamento')) {
          toast.error('Limite de armazenamento atingido', {
            description: 'Libere espaco ou compre mais armazenamento',
            duration: 8000,
          })
          qc.invalidateQueries({ queryKey: ['storage-usage'] })
        } else {
          toast.error(err.message, { duration: 6000 })
        }
      },
    })
  }, [folder.id, uploadToFolder, storageUsage, qc])

  const readEntriesRecursively = useCallback(async (entry: FileSystemEntry): Promise<File[]> => {
    if (entry.isFile) {
      return new Promise((resolve) => {
        (entry as FileSystemFileEntry).file((f) => {
          if (f.type.startsWith('image/') || f.type.startsWith('video/') || f.name.endsWith('.zip') || f.name.endsWith('.rar')) resolve([f])
          else resolve([])
        }, () => resolve([]))
      })
    }
    if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader()
      const entries: FileSystemEntry[] = []
      // readEntries returns batches of max ~100, need to loop
      await new Promise<void>((resolve) => {
        const readBatch = () => {
          reader.readEntries((batch) => {
            if (batch.length === 0) { resolve(); return }
            entries.push(...batch)
            readBatch()
          }, () => resolve())
        }
        readBatch()
      })
      const nested = await Promise.all(entries.map(e => readEntriesRecursively(e)))
      return nested.flat()
    }
    return []
  }, [])

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault()
    dragCounter.current = 0
    setIsDragging(false)

    if (e.dataTransfer.types.includes('application/x-media-images')) return
    if (e.dataTransfer.types.includes('application/x-media-folder')) return

    // Use webkitGetAsEntry to support folder drops (recursively reads subfolders)
    const items = Array.from(e.dataTransfer.items)
    const entries = items.map(item => item.webkitGetAsEntry?.()).filter(Boolean) as FileSystemEntry[]

    if (entries.length > 0 && entries.some(e => e.isDirectory)) {
      toast.info('Lendo arquivos das pastas...')
      const allFiles = await Promise.all(entries.map(entry => readEntriesRecursively(entry)))
      const files = allFiles.flat()
      if (files.length > 0) {
        toast.success(`${files.length} arquivo(s) encontrado(s)`)
        handleUpload(files)
      } else {
        toast.warning('Nenhum arquivo de midia encontrado nas pastas')
      }
      return
    }

    // Fallback: regular file drop
    const files = Array.from(e.dataTransfer.files).filter(f =>
      f.type.startsWith('image/') || f.type.startsWith('video/') ||
      f.name.endsWith('.zip') || f.name.endsWith('.rar')
    )
    if (files.length > 0) handleUpload(files)
  }, [handleUpload, readEntriesRecursively])

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    const allIds = new Set(filteredImages.map(img => img.id))
    setSelected(allIds)
  }

  const selectClips = () => {
    const clipIds = new Set(filteredImages.filter(img => img.clip).map(img => img.id))
    setSelected(clipIds)
  }

  const deselectAll = () => setSelected(new Set())

  // AFP progress tracking
  useEffect(() => {
    if (afpProgress?.status === 'done' && afpJobId) {
      const { processed, failed, total } = afpProgress
      if (failed > 0) {
        toast.warning(`Anti-fingerprint: ${processed - failed}/${total} processadas, ${failed} falharam`)
      } else {
        toast.success(`Anti-fingerprint: ${processed} imagens processadas!`)
      }
      setAfpJobId(null)
      qc.invalidateQueries({ queryKey: ['media-folders'] })
      qc.invalidateQueries({ queryKey: ['storage-usage'] })
      setSelected(new Set())
      setSelectMode(false)
    }
  }, [afpProgress?.status])

  const handleBulkAfp = () => {
    const imageOnly = Array.from(selected).filter(id => {
      const img = folder.images.find(i => i.id === id)
      return img?.mediaType === 'image'
    })
    if (imageOnly.length === 0) {
      toast.error('Selecione pelo menos uma imagem (videos nao suportados)')
      return
    }
    bulkAfp.mutate({ folderId: folder.id, imageIds: imageOnly }, {
      onSuccess: (data) => {
        setAfpJobId(data.jobId)
        toast.info(`Processando ${data.imageCount} imagens...`)
      },
      onError: (err: any) => toast.error(err?.message || 'Erro ao iniciar processamento'),
    })
  }

  const handleBulkDelete = () => {
    if (!confirm(`Deletar ${selected.size} arquivo(s)?`)) return
    bulkDelete.mutate({ folderId: folder.id, imageIds: Array.from(selected) }, {
      onSuccess: (data) => {
        toast.success(`${data.deleted} arquivo(s) removido(s)`)
        setSelected(new Set())
        setSelectMode(false)
        qc.invalidateQueries({ queryKey: ['storage-usage'] })
      },
      onError: () => toast.error('Erro ao deletar'),
    })
  }

  const handleDeleteSingle = (imageId: string) => {
    deleteImage.mutate({ folderId: folder.id, imageId }, {
      onSuccess: () => {
        toast.success('Arquivo removido')
        qc.invalidateQueries({ queryKey: ['storage-usage'] })
      },
    })
  }

  // Pede ao backend o link presigned do R2 (que ja vem com attachment) e
  // dispara o download direto — o arquivo nao passa pelo servidor.
  const handleDownloadSingle = async (img: MediaImage) => {
    try {
      const res = await fetch(`${BASE}/media-images/${img.id}/download`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({ error: 'Erro' }))
        toast.error(body.error || 'Erro ao baixar')
        return
      }
      const { url } = await res.json()
      const a = document.createElement('a')
      a.href = url
      a.click()
    } catch {
      toast.error('Erro ao baixar arquivo')
    }
  }

  const exitSelectMode = () => {
    setSelectMode(false)
    setSelected(new Set())
  }

  const allSelected = filteredImages.length > 0 && selected.size === filteredImages.length
  const clipCount = filteredImages.filter(img => img.clip).length

  return (
    <div
      className="flex flex-1 flex-col overflow-hidden"
      onDragEnter={(e) => {
        e.preventDefault()
        dragCounter.current++
        if (e.dataTransfer.types.includes('Files')) setIsDragging(true)
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => {
        dragCounter.current--
        if (dragCounter.current === 0) setIsDragging(false)
      }}
      onDrop={handleDrop}
    >
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <div className="relative flex-1 min-w-[140px] max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar arquivo..."
            className="h-8 pl-8 text-xs rounded-lg bg-secondary/50 border-border focus:border-primary/30 focus:bg-transparent transition-colors duration-150"
          />
        </div>
        <Select value={mediaTypeFilter} onValueChange={setMediaTypeFilter}>
          <SelectTrigger className="h-8 w-24 text-xs rounded-lg border-border"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="image">Imagens</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
          </SelectContent>
        </Select>
        <Select value={clipFilter} onValueChange={setClipFilter}>
          <SelectTrigger className="h-8 w-24 text-xs rounded-lg border-border"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="clips">Clips</SelectItem>
            <SelectItem value="originals">Originais</SelectItem>
          </SelectContent>
        </Select>
        <Select value={publishedFilter} onValueChange={setPublishedFilter}>
          <SelectTrigger className="h-8 w-28 text-xs rounded-lg border-border"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="published">Publicados</SelectItem>
            <SelectItem value="unpublished">Nao pub.</SelectItem>
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-1.5">
          {!selectMode ? (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs rounded-lg" onClick={() => setSelectMode(true)}>
              <CheckSquare className="h-3.5 w-3.5" /> Selecionar
            </Button>
          ) : (
            <Button variant="ghost" size="sm" className="h-8 gap-1 text-xs rounded-lg" onClick={exitSelectMode}>
              <X className="h-3.5 w-3.5" /> Cancelar
            </Button>
          )}
          {onSchedule && folder.images.length > 0 && (
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs rounded-lg border-border" onClick={onSchedule}>
              <CalendarPlus className="h-3 w-3" /> Agendar
            </Button>
          )}
          <Button
            size="sm"
            className="h-8 gap-1 text-xs rounded-lg"
            disabled={uploadToFolder.isPending}
            onClick={() => uploadRef.current?.click()}
          >
            {uploadToFolder.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            Upload
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1 text-xs rounded-lg"
            disabled={uploadToFolder.isPending}
            onClick={() => folderUploadRef.current?.click()}
          >
            <FolderOpen className="h-3 w-3" />
            Pasta
          </Button>
          <input
            ref={uploadRef}
            type="file"
            multiple
            accept="image/*,video/mp4,video/quicktime,video/webm,.zip,.rar,application/zip,application/x-rar-compressed"
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) {
                handleUpload(Array.from(e.target.files))
                e.target.value = ''
              }
            }}
          />
          <input
            ref={folderUploadRef}
            type="file"
            multiple
            className="hidden"
            {...{ webkitdirectory: '', directory: '' } as any}
            onChange={(e) => {
              if (e.target.files?.length) {
                const files = Array.from(e.target.files).filter(f =>
                  f.type.startsWith('image/') || f.type.startsWith('video/') ||
                  f.name.endsWith('.zip') || f.name.endsWith('.rar')
                )
                if (files.length > 0) {
                  toast.success(`${files.length} arquivo(s) de midia encontrado(s)`)
                  handleUpload(files)
                } else {
                  toast.warning('Nenhum arquivo de midia encontrado na pasta')
                }
                e.target.value = ''
              }
            }}
          />
        </div>
      </div>

      {/* AFP progress */}
      {afpJobId && afpProgress && afpProgress.status !== 'done' && (
        <div className="border-b border-purple-500/20 bg-purple-500/5 px-4 py-3">
          <div className="mb-2 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-purple-500" />
            <span className="text-sm font-medium text-purple-600">
              Anti-fingerprint: {afpProgress.processed}/{afpProgress.total} imagens...
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-purple-500/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 transition-all duration-300 ease-out"
              style={{ width: `${afpProgress.total > 0 ? Math.round((afpProgress.processed / afpProgress.total) * 100) : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload progress */}
      {uploadToFolder.isPending && (
        <div className="border-b border-primary/20 bg-primary/5 px-4 py-3">
          <div className="mb-2 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
            <span className="text-sm font-medium text-primary">
              Enviando {fileCount} arquivo(s)... {uploadProgress}%
            </span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-primary/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-primary to-primary/60 transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Tag bar */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border px-4 py-2.5">
        <Tag className="h-3 w-3 text-muted-foreground shrink-0" />
        {(folder.tags || []).map(tag => (
          <span
            key={tag}
            className="flex items-center gap-1 text-[10px] font-medium bg-primary/10 text-primary/80 px-2 py-0.5 rounded-full transition-colors"
          >
            {tag}
            <button
              className="hover:text-destructive transition-colors"
              onClick={() => handleRemoveTag(tag)}
            >
              <X className="h-2.5 w-2.5" />
            </button>
          </span>
        ))}
        {showTagInput ? (
          <div className="flex items-center gap-1">
            <Input
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              placeholder="Tag..."
              className="h-5 w-24 text-[10px] px-2 rounded-md"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddTag()
                if (e.key === 'Escape') { setShowTagInput(false); setNewTag('') }
              }}
            />
            <Button size="icon" variant="ghost" className="h-5 w-5 rounded-md" onClick={handleAddTag}>
              <Plus className="h-2.5 w-2.5 text-primary" />
            </Button>
            <Button size="icon" variant="ghost" className="h-5 w-5 rounded-md" onClick={() => { setShowTagInput(false); setNewTag('') }}>
              <X className="h-2.5 w-2.5 text-muted-foreground" />
            </Button>
          </div>
        ) : (
          <button
            className="flex items-center gap-0.5 text-[10px] font-medium text-muted-foreground hover:text-foreground px-2 py-0.5 rounded-full border border-dashed border-border/60 hover:border-primary/30 hover:bg-primary/5 transition-colors duration-150"
            onClick={() => setShowTagInput(true)}
          >
            <Plus className="h-2.5 w-2.5" /> tag
          </button>
        )}
        {showTagInput && allExistingTags.filter(t => !(folder.tags || []).includes(t)).length > 0 && (
          <>
            <span className="text-[9px] text-muted-foreground ml-1">|</span>
            {allExistingTags.filter(t => !(folder.tags || []).includes(t)).slice(0, 5).map(tag => (
              <button
                key={tag}
                className="text-[9px] text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded-full bg-muted/30 hover:bg-muted transition-colors font-medium"
                onClick={() => handleAddSuggestion(tag)}
              >
                +{tag}
              </button>
            ))}
          </>
        )}
      </div>

      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-primary/5 backdrop-blur-sm border-2 border-dashed border-primary/30 rounded-xl">
          <div className="flex flex-col items-center gap-3">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-primary/10 border border-primary/20">
              <Upload className="h-8 w-8 text-primary/60" />
            </div>
            <p className="text-sm font-medium text-primary">Solte os arquivos aqui</p>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Subfolders section */}
        {subfolders.length > 0 && onNavigateToFolder && (
          <div className="mb-5">
            <p className="text-sm text-muted-foreground mb-2.5 uppercase tracking-wider font-medium">Subpastas</p>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4">
              {subfolders.map(sub => (
                <button
                  key={sub.id}
                  onClick={() => onNavigateToFolder(sub.id)}
                  className="flex items-center gap-2.5 rounded-xl border border-border bg-card p-3 hover:border-primary/15 transition-colors duration-150 text-left group"
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.currentTarget.classList.add('ring-2', 'ring-primary/40', 'bg-primary/5')
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('ring-2', 'ring-primary/40', 'bg-primary/5')
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    e.currentTarget.classList.remove('ring-2', 'ring-primary/40', 'bg-primary/5')
                    const data = e.dataTransfer.getData('application/x-media-images')
                    if (data) {
                      try {
                        const imageIds: string[] = JSON.parse(data)
                        moveImagesMutation.mutate({ imageIds, targetFolderId: sub.id }, {
                          onSuccess: (res) => toast.success(`${res.moved} arquivo(s) movido(s)`),
                          onError: () => toast.error('Erro ao mover'),
                        })
                      } catch { /* ignore */ }
                    }
                  }}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary/50 border border-border group-hover:border-primary/15 transition-colors duration-150">
                    <FolderOpen className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{sub.name}</p>
                    <p className="text-xs text-muted-foreground">{sub.images.length} arquivos</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {folder.images.length === 0 && subfolders.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-border/60 py-20 hover:border-primary/30 transition-colors duration-150">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-secondary/50 border border-border">
              <Upload className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-muted-foreground">Nenhum arquivo nesta pasta</p>
              <p className="text-xs text-muted-foreground mt-1">Arraste e solte arquivos ou clique no botao Upload</p>
            </div>
            <Button
              size="sm"
              className="gap-1.5 text-xs rounded-xl"
              onClick={() => uploadRef.current?.click()}
            >
              <Upload className="h-3 w-3" /> Fazer upload
            </Button>
          </div>
        ) : folder.images.length === 0 ? (
          null
        ) : filteredImages.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-secondary/50 border border-border">
              <Search className="h-6 w-6 text-muted-foreground/40" />
            </div>
            <p className="text-sm text-muted-foreground">Nenhum resultado para os filtros</p>
          </div>
        ) : (
          <>
            {/* Count + select actions */}
            <div className="mb-3 flex items-center gap-2">
              <Badge variant="outline" className="text-[10px] rounded-lg border-border font-medium">
                {filteredImages.length} {filteredImages.length !== folder.images.length ? `de ${folder.images.length}` : ''} arquivo{filteredImages.length !== 1 ? 's' : ''}
              </Badge>
              {selectMode && (
                <div className="flex items-center gap-1.5">
                  <Button variant="outline" size="sm" className="h-6 px-2.5 text-[10px] rounded-lg border-border" onClick={allSelected ? deselectAll : selectAll}>
                    {allSelected ? 'Desmarcar todos' : 'Selecionar todos'}
                  </Button>
                  {clipCount > 0 && (
                    <Button variant="outline" size="sm" className="h-6 px-2.5 text-[10px] rounded-lg border-border" onClick={selectClips}>
                      Selecionar cortes ({clipCount})
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Grid */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {filteredImages.map((img, idx) => {
                const isSelected = selected.has(img.id)
                const pubStatus = getPublishStatus(img)
                return (
                  <div
                    key={img.id}
                    className={cn(
                      'media-card-animate group relative overflow-hidden rounded-xl border border-border bg-card transition-colors duration-150 cursor-pointer',
                      isSelected
                        ? 'border-primary ring-2 ring-primary/20'
                        : 'hover:border-primary/15',
                    )}
                    style={{ animationDelay: `${Math.min(idx * 30, 300)}ms` }}
                    draggable={!selectMode || selected.size > 0}
                    onDragStart={(e) => {
                      const ids = selected.size > 0 && selected.has(img.id)
                        ? Array.from(selected)
                        : [img.id]
                      e.dataTransfer.setData('application/x-media-images', JSON.stringify(ids))
                      e.dataTransfer.setData('text/plain', `${ids.length} arquivo(s)`)
                      e.dataTransfer.effectAllowed = 'move'
                    }}
                    onClick={() => {
                      if (selectMode) toggleSelect(img.id)
                      else onPreview(img)
                    }}
                  >
                    {/* Thumbnail */}
                    {img.mediaType === 'video' ? (
                      <div className="relative aspect-square bg-secondary rounded-t-xl overflow-hidden">
                        <video src={`${img.url}#t=0.5`} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors duration-150 group-hover:bg-black/40">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 backdrop-blur-sm transition-transform duration-200 group-hover:scale-110">
                            <Play className="h-5 w-5 fill-current text-black ml-0.5" />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-square bg-secondary rounded-t-xl overflow-hidden">
                        <img src={img.url} alt={img.filename} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" loading="lazy" />
                      </div>
                    )}

                    {/* Select checkbox */}
                    {selectMode && (
                      <div className="absolute top-2 left-2 z-10">
                        {isSelected ? (
                          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-primary shadow-md">
                            <Check className="h-3 w-3 text-primary-foreground" />
                          </div>
                        ) : (
                          <div className="flex h-5 w-5 items-center justify-center rounded-md bg-black/40 backdrop-blur-sm border border-white/20">
                            <Square className="h-3 w-3 text-white/70" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Hover actions (not in select mode) */}
                    {!selectMode && (
                      <div className="absolute top-2 right-2 z-10 hidden items-center gap-1 group-hover:flex">
                        <button
                          className="flex h-7 w-7 items-center justify-center rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-white hover:bg-black/70 transition-colors"
                          onClick={(e) => { e.stopPropagation(); onPreview(img) }}
                          title="Preview"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        {img.mediaType === 'video' && (
                          <button
                            className="flex h-7 w-7 items-center justify-center rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-orange-400 hover:bg-black/70 transition-colors"
                            onClick={(e) => { e.stopPropagation(); onCut(img) }}
                            title="Cortar video"
                          >
                            <Scissors className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          className="flex h-7 w-7 items-center justify-center rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-emerald-400 hover:bg-black/70 transition-colors"
                          onClick={(e) => { e.stopPropagation(); handleDownloadSingle(img) }}
                          title="Baixar"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        <button
                          className="flex h-7 w-7 items-center justify-center rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-red-400 hover:bg-black/70 transition-colors"
                          onClick={(e) => { e.stopPropagation(); handleDeleteSingle(img.id) }}
                          title="Remover"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Clip badge */}
                    {img.clip && (
                      <div className="absolute top-2 left-2">
                        <Badge className="border-none bg-black/60 backdrop-blur-sm text-[9px] text-white px-1.5 py-0 font-bold">
                          {img.clip.part}/{img.clip.total}
                        </Badge>
                      </div>
                    )}

                    {/* Cut source badge */}
                    {!selectMode && !img.clip && img.isCutSource && pubStatus === 'none' && (
                      <div className="absolute top-2 left-2 group-hover:hidden">
                        <Badge className="border-none bg-orange-500/80 backdrop-blur-sm text-[9px] text-white px-1.5 py-0 flex items-center gap-0.5 font-semibold">
                          <Scissors className="h-2.5 w-2.5" /> Cortado
                        </Badge>
                      </div>
                    )}

                    {/* Published / Scheduled badge */}
                    {!selectMode && !img.clip && pubStatus === 'published' && (
                      <div className="absolute top-2 left-2 group-hover:hidden">
                        <Badge className="border-none bg-emerald-600/80 backdrop-blur-sm text-[9px] text-white px-1.5 py-0 font-semibold">
                          Publicado
                        </Badge>
                      </div>
                    )}
                    {!selectMode && !img.clip && pubStatus === 'scheduled' && (
                      <div className="absolute top-2 left-2 group-hover:hidden">
                        <Badge className="border-none bg-yellow-500/80 backdrop-blur-sm text-[9px] text-white px-1.5 py-0 flex items-center gap-0.5 font-semibold">
                          <Clock className="h-2.5 w-2.5" /> Agendado
                        </Badge>
                      </div>
                    )}

                    {/* Bottom info */}
                    <div className="p-3">
                      {img.clip ? (
                        <div className="flex items-center gap-1">
                          <span className="truncate text-sm font-medium">{img.clip.source}</span>
                          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                            {formatTime(img.clip.startTime)}-{formatTime(img.clip.endTime)}
                          </span>
                        </div>
                      ) : renamingId === img.id ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <Input
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            className="h-5 flex-1 text-[10px] px-1.5 py-0 rounded-md border-primary/40 bg-transparent"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') submitRename(img)
                              if (e.key === 'Escape') setRenamingId(null)
                            }}
                            onBlur={() => submitRename(img)}
                            disabled={renameImage.isPending}
                          />
                          <span className="text-[9px] text-muted-foreground">
                            {img.filename.substring(img.filename.lastIndexOf('.'))}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1.5 group/name">
                          {img.mediaType === 'video' ? (
                            <Video className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
                          ) : (
                            <ImageIcon className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
                          )}
                          <span className="truncate text-sm font-medium">{img.filename}</span>
                          {!selectMode && (
                            <button
                              className="shrink-0 opacity-0 group-hover/name:opacity-100 transition-opacity"
                              onClick={(e) => { e.stopPropagation(); startRename(img) }}
                              title="Renomear"
                            >
                              <Pencil className="h-2.5 w-2.5 text-muted-foreground hover:text-primary transition-colors" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Bulk action bar */}
      {selectMode && selected.size > 0 && (
        <div className="flex items-center justify-between border-t border-border bg-card px-4 py-3">
          <span className="text-sm font-medium">
            {selected.size} selecionado{selected.size !== 1 ? 's' : ''}
          </span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              className="h-7 gap-1.5 text-xs rounded-lg bg-purple-600 hover:bg-purple-700 text-white"
              onClick={handleBulkAfp}
              disabled={bulkAfp.isPending || !!afpJobId}
            >
              {(bulkAfp.isPending || !!afpJobId) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Shield className="h-3 w-3" />}
              Anti-Fingerprint
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="h-7 gap-1.5 text-xs rounded-lg"
              onClick={handleBulkDelete}
              disabled={bulkDelete.isPending}
            >
              {bulkDelete.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Deletar {selected.size}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
